import { ENGAGEMENT_ANSWER_TYPES } from 'constants/ProjectConstants';

import {
  ADD_SURVEY_DETAILS,
  UPDATE_ANSWER,
  MOVE_TO_NEXT_QUESTION,
  MOVE_TO_PREVIOUS_QUESTION,
  MOVE_TO_QUESTION,
  RESET_STATE,
} from './pulseSurveyActions';

export const pulseSurveyInitialState = {
  endDateFormatted: null,
  name: '',
  questionsData: null,
  currentQuestionId: null,
  requiredQuestionsCount: 0,
  answeredRequiredQuesCount: 0,
  isAnonymous: true,
};

const pulseSurveyReducer = (state = pulseSurveyInitialState, action) => {
  switch (action.type) {
    case ADD_SURVEY_DETAILS: {
      return { ...state, ...getInitDataFromApiData(action.data) };
    }

    case UPDATE_ANSWER: {
      const { questionId, answerId = null, feedbackText = null } = action.data;
      const questionItemToUpdate = state.questionsData.filter((question) => question.id === questionId);
      // get updated selected option
      const selectedAnswerOptions =
        answerId === null
          ? []
          : questionItemToUpdate[0].answerOptions.filter(
              (selectedAnswerOption) => selectedAnswerOption.id === answerId
            );
      const updatedQuestionsData = state.questionsData.map((question) => {
        if (question.id === questionId) {
          return {
            ...question,
            feedbackText,
            selectedAnswerOptions,
          };
        }
        return question;
      });
      // update answered required question count
      const [, newAnsweredRequiredQuestionsCount] = getRequiredQuestionsStat(updatedQuestionsData);
      return {
        ...state,
        questionsData: updatedQuestionsData,
        answeredRequiredQuesCount: newAnsweredRequiredQuestionsCount,
      };
    }

    case MOVE_TO_QUESTION: {
      // also set current question as viewed
      const newQuestionData = state.questionsData.map((item) =>
        item.id === state.currentQuestionId ? { ...item, isVisited: true } : item
      );
      return {
        ...state,
        questionsData: newQuestionData,
        currentQuestionId: action.data.questionId,
      };
    }

    case MOVE_TO_NEXT_QUESTION: {
      // also set current question as viewed
      const newQuestionData = state.questionsData.map((item) =>
        item.id === state.currentQuestionId ? { ...item, isVisited: true } : item
      );
      return {
        ...state,
        questionsData: newQuestionData,

        currentQuestionId: getNextQuestionId(state.questionsData, state.currentQuestionId),
      };
    }

    case MOVE_TO_PREVIOUS_QUESTION: {
      // also set current question as viewed
      const newQuestionData = state.questionsData.map((item) =>
        item.id === state.currentQuestionId ? { ...item, isVisited: true } : item
      );
      return {
        ...state,
        questionsData: newQuestionData,
        currentQuestionId: getPreviousQuestionId(state.questionsData, state.currentQuestionId),
      };
    }

    case RESET_STATE: {
      return { ...action.data };
    }

    default:
      return state;
  }
};

export default pulseSurveyReducer;

// ************** HELPERS ******************
const flattenQuestionData = (questionData) => {
  const { question, isRequired } = questionData;

  const {
    id,
    description,
    answerOptions,
    answerType,
    enableFeedbackText,
    selectedAnswerOptions,
    feedbackText,
    isTextRequired,
  } = question;

  return {
    id,
    isRequired,
    description,
    answerOptions,
    answerType,
    enableFeedbackText,
    selectedAnswerOptions: selectedAnswerOptions || [],
    feedbackText,
    isTextRequired,
    // intoduced here for keeping record of viewed(both skipped and answered) questions in frontend
    isVisited: false,
  };
};

const getInitDataFromApiData = (data) => {
  const { endDateFormatted, name, questions, isAnonymous } = data;
  const questionsData = questions.map((questionData) => flattenQuestionData(questionData));
  const currentQuestionId = getFistQuestionIdToDisplay(questionsData);
  const [requiredQuestionsCount, answeredRequiredQuesCount] = getRequiredQuestionsStat(questionsData);
  return {
    endDateFormatted,
    isAnonymous,
    name,
    questionsData,
    currentQuestionId,
    requiredQuestionsCount,
    answeredRequiredQuesCount,
  };
};

const getNextQuestionId = (questionsData, currentQuestionId) => {
  const totalQuestions = questionsData.length;
  const currentQuestionIndex = questionsData.findIndex((question) => question.id === currentQuestionId);
  if (currentQuestionIndex + 1 < totalQuestions) {
    return questionsData[currentQuestionIndex + 1].id;
  }
  return currentQuestionId;
};

const getPreviousQuestionId = (questionsData, currentQuestionId) => {
  const currentQuestionIndex = questionsData.findIndex((question) => question.id === currentQuestionId);
  if (currentQuestionIndex - 1 >= 0) {
    return questionsData[currentQuestionIndex - 1].id;
  }
  return currentQuestionId;
};

const getRequiredQuestionsStat = (questionsData) => {
  let answeredRequiredQuesCount = 0;
  let requiredQuesCount = 0;

  const isRequiredQuestionAnswered = ({ selectedAnswerOptions, feedbackText, answerType, isTextRequired }) => {
    if (answerType === ENGAGEMENT_ANSWER_TYPES.NONE) return feedbackText !== null;

    if (isTextRequired) return selectedAnswerOptions.length !== 0 && feedbackText !== null;
    return selectedAnswerOptions.length !== 0;
  };

  questionsData.forEach((question) => {
    const isRequiredQues = question.isRequired;
    if (isRequiredQues) {
      // we are only counting required answered questions
      requiredQuesCount += 1;

      if (isRequiredQuestionAnswered(question)) {
        answeredRequiredQuesCount += 1;
      }
    }
  });

  return [requiredQuesCount, answeredRequiredQuesCount];
};

const getFistQuestionIdToDisplay = (questionsData) => {
  const firstUnansweredQuestionIndex = questionsData.findIndex(
    (question) =>
      question.selectedAnswerOptions.length === 0 || (question.isTextRequired && question.feedbackText === null)
  );
  if (firstUnansweredQuestionIndex === -1) {
    // all answered - show last question
    return questionsData[questionsData.length - 1].id;
  }
  return questionsData[firstUnansweredQuestionIndex].id;
};
